/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn } from '@fluentui/react/lib/DetailsList';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { QnaMakerSourceStore } from 'src/qnaMaker/core/stores/QnaMakerSourceStore';

export type QnaManageSourcesTableProps = TableProps<QnaMakerSourceStore> & {
    onSourceClick: (sourceId: string) => void;
    isSourceLinkAttributeName: string;
};

const messages = defineMessages({
    sourceColumnHeader: {
        id: 'qnaMaker.qnaManageSourcesTable.sourceColumnHeader',
        defaultMessage: 'Source'
    },
    sourceNameHeader: {
        id: 'qnaMaker.qnaManageSourcesTable.sourceNameHeader',
        defaultMessage: 'Source name'
    },
    unstructuredHeader: {
        id: 'qnaMaker.qnaManageSourcesTable.unstructuredHeader',
        defaultMessage: 'Unstructured'
    },
    sourceTypeHeader: {
        id: 'qnaMaker.qnaManageSourcesTable.sourceTypeHeader',
        defaultMessage: 'Source type'
    },
    yes: {
        id: 'qnaMaker.qnaManageSourcesTable.yes',
        defaultMessage: 'Yes'
    },
    no: {
        id: 'qnaMaker.qnaManageSourcesTable.no',
        defaultMessage: 'No'
    }
});

export const QnaManageSourcesTable = (props: QnaManageSourcesTableProps) => {
    const { selection, items, onSourceClick, sortKey, isSourceLinkAttributeName, ...rest } = props;
    const { localize } = useLocalization();

    const columns: IColumn[] = [
        {
            key: 'source',
            minWidth: 400,
            fieldName: 'source',
            isRowHeader: true,
            isResizable: true,
            name: localize(messages.sourceColumnHeader),
            isSorted: sortKey && sortKey.key === 'source',
            isSortedDescending: sortKey && sortKey.key === 'source' && sortKey.order === 'desc',
            onRender: (item: QnaMakerSourceStore) => (
                <NameCellLink {...{ [isSourceLinkAttributeName]: 'true' }} onClick={() => onSourceClick(item.id)}>
                    <TruncatedText title={item.source} maxWidth={350}>
                        {item.source}
                    </TruncatedText>
                </NameCellLink>
            )
        },
        {
            minWidth: 200,
            isResizable: true,
            key: 'name',
            fieldName: 'name',
            name: localize(messages.sourceNameHeader),
            isSorted: sortKey && sortKey.key === 'name',
            isSortedDescending: sortKey && sortKey.key === 'name' && sortKey.order === 'desc',
            onRender: (item: QnaMakerSourceStore) => (
                <TruncatedText title={item.name} maxWidth={350}>
                    {item.name}
                </TruncatedText>
            )
        },
        {
            minWidth: 200,
            key: 'contentStructureKind',
            isResizable: true,
            fieldName: 'contentStructureKind',
            name: localize(messages.unstructuredHeader),
            isSorted: sortKey && sortKey.key === 'contentStructureKind',
            isSortedDescending: sortKey && sortKey.key === 'contentStructureKind' && sortKey.order === 'desc',
            onRender: (item: QnaMakerSourceStore) => (
                <LuisText>{localize(item.contentStructureKind === 'unstructured' ? messages.yes : messages.no)}</LuisText>
            )
        },
        {
            minWidth: 200,
            key: 'kind',
            isResizable: true,
            fieldName: 'kind',
            name: localize(messages.sourceTypeHeader),
            isSorted: sortKey && sortKey.key === 'kind',
            isSortedDescending: sortKey && sortKey.key === 'kind' && sortKey.order === 'desc'
        }
    ];

    return <Table<QnaMakerSourceStore> items={items} columns={columns} selection={selection} {...rest} />;
};
